import type {
  BankDocumentEntryQueueDto,
  BankDocumentQueueStatus,
} from '../Types/bankDocument.types';

export type ResolvedBankDocumentQueueStatus =
  | 'readyComplete'
  | 'readyPartial'
  | 'blocked'
  | 'postedWithUnassigned'
  | 'posted'
  | 'skipped'
  | 'unknown';

export interface ResolvedBankDocumentQueueState {
  status: ResolvedBankDocumentQueueStatus;
  diagnosticCode: string | null;
  hasUnassignedRemainder: boolean;
  usedLegacyFallback: boolean;
}

const CANONICAL_STATUSES: readonly BankDocumentQueueStatus[] = [
  'posted',
  'readyComplete',
  'readyPartial',
  'blocked',
  'postedWithUnassigned',
  'skipped',
  'unknown',
];

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const toNullableNumber = (value: unknown): number | null => {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const hasUnassignedRemainder = (item: BankDocumentEntryQueueDto): boolean => {
  const remainder = toNullableNumber(item.unassignedRemainderAmount);
  if (remainder !== null) {
    return remainder > 0;
  }

  const remainingAmount = toNullableNumber(item.remainingAmount);
  const allocatedAmount = toNullableNumber(item.allocatedAmount);
  if (item.amount <= 0) {
    return false;
  }

  if (remainingAmount !== null) {
    return remainingAmount > 0;
  }

  if (allocatedAmount !== null) {
    return allocatedAmount > 0 && allocatedAmount < item.amount;
  }

  return false;
};

const fallbackFromLegacyStatus = (
  item: BankDocumentEntryQueueDto,
  hasRemainder: boolean
): ResolvedBankDocumentQueueStatus => {
  if (item.postingStatus === 'failed') {
    return 'blocked';
  }

  if (item.postingStatus === 'posted') {
    if (item.isPosted) {
      return hasRemainder ? 'postedWithUnassigned' : 'posted';
    }

    if (item.amount > 0 && hasRemainder) {
      return 'readyPartial';
    }

    return 'readyComplete';
  }

  if (item.postingStatus === 'skipped') {
    return 'skipped';
  }

  if (item.isPosted) {
    return hasRemainder ? 'postedWithUnassigned' : 'posted';
  }

  return normalizeString(item.diagnosticCode) ? 'blocked' : 'unknown';
};

const mapCanonicalStatus = (
  rawQueueStatus: string,
  item: BankDocumentEntryQueueDto,
  hasRemainder: boolean
): ResolvedBankDocumentQueueStatus => {
  if (rawQueueStatus === 'posted') {
    if (item.isPosted) {
      return hasRemainder ? 'postedWithUnassigned' : 'posted';
    }

    return hasRemainder ? 'readyPartial' : 'readyComplete';
  }

  if (CANONICAL_STATUSES.includes(rawQueueStatus as BankDocumentQueueStatus)) {
    return rawQueueStatus as ResolvedBankDocumentQueueStatus;
  }

  return 'unknown';
};

export const resolveBankDocumentQueueState = (
  item: BankDocumentEntryQueueDto
): ResolvedBankDocumentQueueState => {
  const hasRemainder = hasUnassignedRemainder(item);
  const normalizedDiagnosticCode = normalizeString(item.diagnosticCode);
  const normalizedQueueStatus = normalizeString(item.queueStatus);
  const hasCanonicalQueueStatus = normalizedQueueStatus !== null;

  const status = hasCanonicalQueueStatus
    ? mapCanonicalStatus(normalizedQueueStatus, item, hasRemainder)
    : fallbackFromLegacyStatus(item, hasRemainder);

  const diagnosticCode =
    status === 'readyPartial' || status === 'postedWithUnassigned'
      ? null
      : normalizedDiagnosticCode;

  return {
    status,
    diagnosticCode,
    hasUnassignedRemainder: hasRemainder,
    usedLegacyFallback: !hasCanonicalQueueStatus,
  };
};

export const isQueueEntryReadyToPost = (item: BankDocumentEntryQueueDto): boolean => {
  if (item.isPosted) {
    return false;
  }

  const status = resolveBankDocumentQueueState(item).status;
  return status === 'readyComplete' || status === 'readyPartial';
};

export const resolveQueueStatusLabel = (
  queueState: ResolvedBankDocumentQueueState,
  isPosted: boolean
): string => {
  if (queueState.status === 'readyComplete') {
    return isPosted ? 'Zaksięgowana' : 'Gotowa do księgowania';
  }

  if (queueState.status === 'readyPartial') {
    return 'Gotowa do księgowania (z resztą)';
  }

  if (queueState.status === 'postedWithUnassigned') {
    return 'Zaksięgowana z nieprzypisaną resztą';
  }

  if (queueState.status === 'posted') {
    return 'Zaksięgowana';
  }

  if (queueState.status === 'skipped') {
    return 'Pominięta';
  }

  if (
    queueState.status === 'blocked' &&
    queueState.diagnosticCode === 'BANK_ENTRY_ALLOCATION_INCOMPLETE'
  ) {
    return 'Niekompletna alokacja';
  }

  if (queueState.status === 'blocked') {
    return 'Błąd walidacji';
  }

  return 'Wymaga weryfikacji';
};

export const resolveQueueStatusColor = (
  queueState: ResolvedBankDocumentQueueState
): 'error' | 'success' | 'warning' => {
  if (queueState.status === 'blocked') {
    return 'error';
  }

  if (queueState.status === 'readyComplete' || queueState.status === 'posted') {
    return 'success';
  }

  return 'warning';
};
